import cn from 'classnames';
import * as S from "../AgentChat.styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { Button } from "@material-ui/core";
import { Send, AttachFile, Close } from '@mui/icons-material';
import { messageSend, readMessage, openChatPage } from "socket/socket";
import { messagesSort } from "../../ChatDataList/ChatDataList.config";
import { ChatDataList } from "components";
import { isPersian } from "utils/functions";
import { draftManager } from "utils/draftManager";


export const CurrentAgentChat = ({ chatData, agent })=> {
  const {list: shortcuts} = useSelector(({ shortcut }) => shortcut);

  const [selectedFile, setSelectedFile] = useState('');
  const [file, setFile] = useState(null);
  const [textDirection, setTextDirection] = useState('ltr');
  const [wordToCompile, setWordToCompile] = useState('');
  const [filteredShortcuts, setFilteredShortcuts] = useState(shortcuts || []);
  const [activeShortcut, setActiveShortcut] = useState(null);
  const [selectedShortcut, setSelectedShortcut] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const shortcutsContainer = useRef();
  const isShortcutWord = useRef(false);
  const textAreaRef = useRef();
  const sortedMessages = useMemo(() => messagesSort(chatData.messages || []), [chatData.messages]);

  const arrangeShortcut = (data) => {
    let msg = data.message;
    data?.options.forEach(option => { msg += `\n[option]: ${option}` });
    return msg;
  };

  const handleChangeFile = useCallback((e) => {
    const fileToUpload = e.target.files[0];
    if(fileToUpload.size <= 4000000) {
      setFile(fileToUpload);
      const reader = new FileReader();
      reader.readAsDataURL(fileToUpload);
      reader.onloadend = function() { setSelectedFile(reader.result); }
    }
    e.target.value = null;
  }, []);

  const handleChange = useCallback((e) => {
    const char = e.target.value.trim();
    draftManager.saveDraft(chatData._id, e.target.value);

    if (char.charAt(0) === '/') { isShortcutWord.current = true; setCurrentIndex(0); }
    if (char.charAt(0) !== '/' && isShortcutWord.current) isShortcutWord.current = false;
    if (isPersian(char.charAt(0))) setTextDirection('rtl');
    else setTextDirection('ltr');
    if (shortcutsContainer.current) shortcutsContainer.current.scrollTo(0, 0);
    if (selectedShortcut) { setSelectedShortcut(null); setActiveShortcut(null); }
  }, [selectedShortcut, chatData._id]);

  const handleKeyUp = useCallback((e) => {
    if (activeShortcut || e.target.value.trim().charAt(0) === '/') {
      const words = e.target.value.trim().split(' ');
      setWordToCompile(words[0].slice(1));

      if (words.length !== 1 || !e.target.value.trim()) {
        isShortcutWord.current = false;
        setWordToCompile('');
        setActiveShortcut(null);
      }

      if (e.key === 'ArrowDown' && filteredShortcuts.length - 1 !== currentIndex && shortcutsContainer.current) {
        shortcutsContainer.current.scrollTo(0, currentIndex * 36);
        setCurrentIndex(prev => prev + 1);
      }
      if (e.key === 'ArrowUp' && currentIndex !== 0 && shortcutsContainer.current) {
        const Y = shortcutsContainer.current.scrollHeight - (36 * (filteredShortcuts.length + 1 - currentIndex));
        shortcutsContainer.current.scrollTo(0, Y);
        setCurrentIndex(prev => prev - 1);
      }
      if (e.key === 'Enter' && filteredShortcuts.length) {
        setSelectedShortcut(activeShortcut);
        setActiveShortcut(null);
        setCurrentIndex(0);
        textAreaRef.current.value = arrangeShortcut(activeShortcut);
      }
    }
  }, [currentIndex, filteredShortcuts.length, activeShortcut]);

  const handleSendShortcut = useCallback((shortcut) => {
    textAreaRef.current.value = arrangeShortcut(shortcut);
    textAreaRef.current.focus();
    setSelectedShortcut(shortcut);
    setActiveShortcut(null);
    setCurrentIndex(0);
    setWordToCompile('');
    isShortcutWord.current = false;
  }, []);

  useEffect(() => {
    if (filteredShortcuts.length && isShortcutWord.current) setActiveShortcut(filteredShortcuts[currentIndex]);
  }, [filteredShortcuts, currentIndex]);

  useEffect(() => {
    if (isShortcutWord.current) setFilteredShortcuts(shortcuts?.filter(s => s.tag.includes(wordToCompile)));
  }, [wordToCompile, shortcuts]);

  useEffect(()=> {    
    openChatPage(chatData?._id);
    if (textAreaRef.current && chatData?._id) {
      textAreaRef.current.value = draftManager.getDraft(chatData._id);
    }
  }, [chatData?._id]);

  useEffect(()=>{
    readMessage(chatData?._id);
  },[chatData]);

  const onEnterPress = (e) => {
    if (e.key === 'Enter' && e.shiftKey === false && (e.target.value.trim() || file)) {
      e.preventDefault();
      if (!isShortcutWord.current && !selectedShortcut) {
        send();
      } else if (selectedShortcut) {
        messageSend(chatData._id, arrangeShortcut(selectedShortcut));
        draftManager.clearDraft(chatData._id);
        isShortcutWord.current = false;
        textAreaRef.current.value = '';
      }
    }
  };

  const send = () => {
    const message = textAreaRef.current.value;
    if ((message.trim() || file) && chatData?._id) {
      messageSend(chatData._id, message.trim(), file);
      setFile('');
      setSelectedFile('');
      textAreaRef.current.value = '';
      draftManager.clearDraft(chatData._id);
    }
  };

  return(
    <S.ChatContent>
      <ChatDataList
        messagesChat={sortedMessages}
        agentName={agent.name}
        chatId={chatData._id}
        chatCompanionId={chatData.companion._id}
        chatLogUsers={chatData.logUsers}
      />
      {!chatData?.chatEnd && <S.EditorContainer>
          <S.TextArea
            ref={textAreaRef}
            minRows={6}
            onKeyDown={onEnterPress}
            onKeyUp={handleKeyUp}
            style={{ direction: textDirection }}
            onChange={handleChange}
          />
          {isShortcutWord.current && wordToCompile &&
            <S.ShortcutSelect>
              <S.ShortcutContainer ref={shortcutsContainer}>
                {filteredShortcuts.map((shortcut, index) =>
                  <S.MenuItem
                    key={index}
                    className={cn({isSelected: shortcut?._id === activeShortcut?._id})}
                    onClick={() => handleSendShortcut(shortcut)}
                  >
                    {shortcut.message}
                  </S.MenuItem>)
                }
              </S.ShortcutContainer>
            </S.ShortcutSelect>
          }
          <S.EditorButtonsContainer>
            <Button aria-label="Attach file">
              <S.InputLabel htmlFor="agent-file-input">
                <AttachFile />
              </S.InputLabel>
              <S.FileInput
                accept="video/*, image/*"
                id="agent-file-input"
                max="500"
                type="file"
                onChange={handleChangeFile}
              />
            </Button>
            <Button onClick={send}>
              <Send />
            </Button>
          </S.EditorButtonsContainer>
        </S.EditorContainer>}
        {selectedFile &&
          <S.FileContent>
            <Close onClick={() => {
              setSelectedFile('');
              setFile('');
              document.getElementById("agent-file-input").value = null;
            }} />
            {file.type?.split('/')[0] === 'image' ?
              <S.SelectedImage src={selectedFile} /> : <S.SelectedVideo src={selectedFile} />
            }
          </S.FileContent>
        }
    </S.ChatContent>
  )
}